import Head from "next/head";
import dynamic from "next/dynamic";
import Theme from "../components/Theme";
import People from "../components/organisms/common/People";
import Masonry from "../components/organisms/common/Masonry";
import ResponsiveImage from "../components/atoms/image/ResponsiveImage";
import LoudVideoSection from "../components/organisms/common/LoudVideoSection";
import AppStorage from "../components/AppStorage";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../styles/cssinjs/helpers/variables.js";
const VideoSchema = dynamic(() =>
  import("../components/organisms/common/VideoSchema")
);
const AdaptiveCriticalImage = dynamic(() =>
  import("../components/atoms/AdaptiveCriticalImage")
);
const WideCTA = dynamic(() => import("../components/organisms/common/WideCTA"));
const TpWidget = dynamic(() => import("../components/atoms/document/TpWidget"));
const TpWidgetMini = dynamic(() =>
  import("../components/organisms/common/TpWidgetMini")
);
export async function getStaticProps(ctx) {
  let allData = {};
  const { locale } = ctx;
  // Page Route
  const route = "partners";

  // Fetch data for respective pages
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );

  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );

  const pricingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/pricing.json`
  );

  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/others/${route}/marketing.json`
  );

  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/others/${route}/content.json`
  );

  // Catch Error
  if (marketingResponse.status === 404 || contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }
  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();
  allData.pricing = await pricingResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };
  const pricing = {
    pricing: allData.pricing,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...pricing,
    ...allData.marketing,
    ...allData.content,
  };

  // Return to the component
  return {
    props: {
      data,
    },
  };
}

export default function Partners({ data, nonce }) {
  const { seo, header, footer, pricing, content } = data;
  const Hero = ({ data }) => {
    return (
      <section
        className={`container grid--default hero--default lg:pb-10 md:pb-5 has-align-top`}
      >
        <article className={`lg:maxw-lg text-left`}>
          <h1 className="text-xl lg:pt-5">{data.title}</h1>
          {data.hasOwnProperty("desc") &&
            data.desc.map((item, index) => {
              return (
                <p
                  className="lg:maxw-md lg:mt-2 lg:mb-2 md:mb-2"
                  key={index}
                  dangerouslySetInnerHTML={{ __html: item }}
                />
              );
            })}
          {data.hasOwnProperty("hasTpWidget") && <TpWidgetMini />}
        </article>{" "}
        <figure className="md:mt-5 lg:maxw-lg">
          <picture>
            <AdaptiveCriticalImage
              src={data.img.src}
              alt={data.img.alt}
              mobile={data.img.prefixes.mobile}
              dpi={data.img.prefixes.dpi}
              dimensions={data.img.dimensions}
              className="img-fit"
            />
          </picture>
        </figure>
      </section>
    );
  };
  const TeamCards = ({ data }) => {
    return (
      <div className="container lg:pb-15 md:pb-10">
        <article className="lg:maxw-lg mx-auto lg:text-center md:text-left">
          <h2
            className="text-lg"
            dangerouslySetInnerHTML={{ __html: data.title }}
          ></h2>
        </article>
        <div className="lg:mt-10 md:mt-5 card--default card--showcase has-three-col has-gutter-md c-padding">
          {data.list.map((item, index, items) => {
            return (
              <a
                href={item.href}
                key={index}
                className={`hover-review-card md:w-100 ${
                  index === items.length - 1 ? "boxshadow-none bg-light" : ""
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="fx">
                  <figure dangerouslySetInnerHTML={{ __html: item.icon }} />
                  <h3 className="text-brandcolor lg:mb-3 md:mb-1.5">
                    {item.title}
                  </h3>
                </div>
                <p dangerouslySetInnerHTML={{ __html: item.desc }} />
              </a>
            );
          })}
        </div>
      </div>
    );
  };
  const AffiliateCTA = ({ data }) => {
    return (
      <div className="container grid--default has-align-middle lg:pb-20 md:pb-5 md:fx md:fx--w">
        <article className="text-left lg:maxw-md fx--o-2">
          <h2
            className="text-lg lg:mb-3 md:mb-2 md:mt-5"
            dangerouslySetInnerHTML={{ __html: data.title }}
          />
          <p
            dangerouslySetInnerHTML={{ __html: data.desc }}
            className="lg:mb-4 md:mb-3"
          />
          <a href={data.cta.href} className="btn-primary md:w-100">
            <span>{data.cta.text}</span>
          </a>
        </article>{" "}
        <ResponsiveImage
          src={data.image.src}
          alt={data.image.alt}
          dpi={data.image.prefixes.dpi}
          mobile={data.image.prefixes.mobile}
          width="560"
          height="400"
          imgClass="img-fit"
          hasMobileImg={false}
        />
      </div>
    );
  };

  const SocialLinkSection = ({ data }) => {
    return (
      <>
        <section>
          <h2
            className="container text-center text-xl lg:mb-5 md:mb-2.5"
            dangerouslySetInnerHTML={{ __html: data.title }}
          />
          <article className="social-media-section bg-offset-bottom">
            <figure className="container">
              {data.socialLinks.map((item, index) => {
                return (
                  <a
                    href={item.href}
                    key={index}
                    target="_blank"
                    rel="noopener noreferrer"
                    dangerouslySetInnerHTML={{ __html: item.icon }}
                  ></a>
                );
              })}
            </figure>
          </article>
        </section>
        <section className="bg-light lg:pt-5 lg:pb-10 md:pb-10 text-center">
          <div className="container card--default card--feature has-three-col lg:mb-5 md:mb-2.5">
            {data.list.map((item, index) => {
              return (
                <a
                  href={item.href}
                  key={index}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="br-8 light-shadow bg-white md:mb-0 md:mt-2.5 br-8 text-left lg:px-3 lg:py-3 md:pt-2 md:pl-2.5 md:pr-2 md:pb-3 blog-posts"
                >
                  <h3 className="lg:mb-2 md:mb-1.5 text-brandcolor fw-700">
                    {item.title}
                  </h3>
                  <p>{item.desc}</p>
                </a>
              );
            })}
          </div>
          <a
            href={data.more.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-brandcolor"
          >
            {data.more.text}
          </a>
        </section>
        <style jsx>{`
          .social-media-section {
            max-width: initial;
          }
          .social-media-section figure {
            width: auto;
            max-width: 440px;
            justify-content: space-between;
          }
          .social-media-section figure a {
            padding: 20px;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .social-media-section figure a {
              padding: 15px;
            }
          }
        `}</style>
      </>
    );
  };
  return (
    <Theme
      header={header}
      footer={footer}
      seo={seo}
      nonce={nonce}
      className="partners industry-page-with-trust-pilot"
    >
      <AppStorage pageName="Partners Page" />
      <Hero data={content.hero} />
      <section className="lg:pt-10 md:pt-5 bg-light pcustom img-wd-mob bg-light">
        <TeamCards data={content.teamCards} />
        <AffiliateCTA data={content.affiliateCTA} />
      </section>
      <LoudVideoSection
        data={content.loudVideoSection}
        className="md:fx md:fx--w bg-offset-top"
      />
      <Masonry
        data={content.masonry}
        className="lg:mt-20 md:mt-9 lg:mb-20 md:mb-10"
      />
      <SocialLinkSection data={content.socialLinkSection} />
      <People className="bg-light" />
      <WideCTA data={content.wideCTA} />
      <Head>
        <VideoSchema data={content.loudVideoSection} />
      </Head>
      <TpWidget />
    </Theme>
  );
}
